import React, { useEffect, useState, useCallback } from 'react';
import styles from './styles.module.css';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Skeleton,
  InputBase,
  Paper,
  Divider,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TuneIcon from '@mui/icons-material/Tune';
import StarIcon from '@mui/icons-material/Star';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ShopService from '../../../services/ShopService';
import SanoftLogo from '../../icons/Logo/sanoft_logo.png';

const ShopListPage = () => {
  const history = useHistory();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [cities, setCities] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  const [radius, setRadius] = useState(50);
  const [searchFocused, setSearchFocused] = useState(false);

  const sortCitiesByDistance = (lat, lng, cityList) => {
    if (!cityList || cityList.length === 0) return [];
    return [...cityList].sort((a, b) => {
      const distA = Math.pow(a.latitude - lat, 2) + Math.pow(a.longitude - lng, 2);
      const distB = Math.pow(b.latitude - lat, 2) + Math.pow(b.longitude - lng, 2);
      return distA - distB;
    });
  };

  // Menu state for location picker
  const [anchorEl, setAnchorEl] = useState(null);
  const openLocationMenu = Boolean(anchorEl);

  const fetchShops = useCallback(async (latitude, longitude, dist = radius, cityId = null) => {
    try {
      setLoading(true);
      const data = await ShopService.getNearestShops(latitude, longitude, 50, dist, false, cityId);
      let shopList = Array.isArray(data) ? data : [];
      
      // Explicitly sort by distance ascending if distance is available
      if (latitude && longitude) {
        shopList.sort((a, b) => (a.distance || 0) - (b.distance || 0));
      }
      
      setShops(shopList);
    } catch (err) {
      console.error('Failed to fetch shops:', err);
    } finally {
      setLoading(false);
    }
  }, [radius]);

  useEffect(() => {
    const initData = async () => {
      let cityList = [];
      try {
        const cityData = await ShopService.getCities();
        cityList = Array.isArray(cityData) ? cityData : [];
        setCities(cityList);
      } catch (err) {
        console.error('Failed to fetch cities:', err);
      }

      const defaultFallback = () => {
        const defaultCity = cityList.length > 0 ? cityList[0] : null;
        if (defaultCity) {
          setSelectedLocation({
            id: defaultCity.id,
            name: defaultCity.name,
            latitude: defaultCity.latitude,
            longitude: defaultCity.longitude
          });
          fetchShops(defaultCity.latitude, defaultCity.longitude, radius, defaultCity.id);
        } else {
          fetchShops();
        }
      };

      if (navigator.geolocation) {
        setLoading(true);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;
            const sortedCities = sortCitiesByDistance(latitude, longitude, cityList);
            setCities(sortedCities);
            const closestCity = sortedCities.length > 0 ? sortedCities[0] : null;
            setSelectedLocation({
              id: closestCity ? closestCity.id : null,
              name: closestCity ? closestCity.name : "Current Location",
              latitude,
              longitude
            });
            fetchShops(latitude, longitude, radius, closestCity ? closestCity.id : null);
          },
          (error) => {
            console.error('Geolocation error:', error);
            defaultFallback();
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      } else {
        defaultFallback();
      }
    };

    initData();
  }, [fetchShops, radius]);

  const handleLocationClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCitySelect = (city) => {
    setSelectedLocation({
      id: city.id,
      name: city.name,
      latitude: city.latitude,
      longitude: city.longitude
    });
    fetchShops(city.latitude, city.longitude, radius, city.id);
    setSearchKey('');
    setSearchFocused(false);
  };

  const matchingCities = searchKey.trim()
    ? cities.filter((city) => city.name.toLowerCase().includes(searchKey.toLowerCase()))
    : [];

  const handleLocationClose = (location) => {
    setAnchorEl(null);
    if (location && location.latitude && location.longitude) {
      setSelectedLocation(location);
      setSearchKey('');
      setSearchFocused(false);
      fetchShops(location.latitude, location.longitude, radius, location.id);
    }
  };

  const handleShopClick = (shopId) => {
    history.push(`/${shopId}`);
  };

  return (
    <div className={styles.container}>
      {/* Sticky Header */}
      <header className={styles.header}>
        <div className={styles.navWrapper}>
          <img src={SanoftLogo} alt="Logo" className={styles.logo} onClick={() => history.push('/')} />

          <Paper className={styles.searchContainer} elevation={0}>
            <div className={styles.locationPicker} onClick={handleLocationClick}>
              <LocationOnIcon className={styles.locationIcon} />
              <div className={styles.locationDisplay}>
                <span>
                  {selectedLocation?.name || "Select Location"}
                </span>
                <ArrowDropDownIcon />
              </div>
            </div>

            <Menu
              anchorEl={anchorEl}
              open={openLocationMenu}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                style: {
                  width: '300px',
                  borderRadius: '12px',
                  marginTop: '8px',
                  padding: '4px'
                },
              }}
            >
              <MenuItem 
                disabled={loading}
                onClick={() => {
                  if (navigator.geolocation) {
                    setLoading(true);
                    navigator.geolocation.getCurrentPosition((pos) => {
                      const { latitude, longitude } = pos.coords;
                      
                      const sortedCities = sortCitiesByDistance(latitude, longitude, cities);
                      setCities(sortedCities);
                      const cityName = sortedCities.length > 0 ? sortedCities[0].name : "Current Location";
                      
                      handleLocationClose({
                        id: sortedCities.length > 0 ? sortedCities[0].id : null,
                        name: cityName,
                        latitude,
                        longitude
                      });
                    }, (err) => {
                      console.error('Geolocation error in manual click:', err);
                      setLoading(false);
                      handleLocationClose();
                    }, { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 });
                  }
                }} 
                className={styles.currentLocationItem}
              >
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {loading ? (
                    <CircularProgress size={20} sx={{ mr: 1.5, color: '#ef4f5f' }} />
                  ) : (
                    <LocationOnIcon sx={{ color: '#ef4f5f', mr: 1.5 }} />
                  )}
                  <div className={styles.currentLocationText}>
                    <span className={styles.useCurrentText}>
                      {loading ? 'Fetching location...' : 'Use current location'}
                    </span>
                    <span className={styles.usingGPSText}>Auto-fetch coordinates</span>
                  </div>
                </div>
              </MenuItem>

              {cities.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <div style={{ padding: '8px 16px', fontSize: '11px', color: '#9c9c9c', textTransform: 'uppercase', fontWeight: 600 }}>
                    Cities
                  </div>
                  {cities.map((city) => (
                    <MenuItem 
                      key={city.id} 
                      onClick={() => {
                        handleLocationClose({
                          id: city.id,
                          name: city.name,
                          latitude: city.latitude,
                          longitude: city.longitude
                        });
                      }} 
                      className={styles.locationMenuItem}
                    >
                      <LocationOnIcon sx={{ color: '#9c9c9c', mr: 1.5, fontSize: 20 }} />
                      <div className={styles.currentLocationText}>
                        <span className={styles.cityNameText}>{city.name}</span>
                      </div>
                    </MenuItem>
                  ))}
                </>
              )}
            </Menu>

            <Divider orientation="vertical" flexItem className={styles.searchDivider} />

            <div className={styles.restaurantSearch}>
              <SearchIcon className={styles.searchIcon} />
              <InputBase
                placeholder="Search for shops, products or cities"
                className={styles.searchInput}
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
              />
              {searchFocused && matchingCities.length > 0 && (
                <div className={styles.searchSuggestions}>
                  <div className={styles.suggestionsHeader}>
                    <LocationOnIcon sx={{ fontSize: 16, color: '#ef4f5f' }} />
                    Cities
                  </div>
                  {matchingCities.map((city) => (
                    <div
                      key={city.id}
                      className={styles.suggestionItem}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleCitySelect(city)}
                    >
                      <LocationOnIcon sx={{ fontSize: 18, color: '#9c9c9c', mr: 1 }} />
                      <div className={styles.suggestionText}>
                        <span className={styles.suggestionName}>{city.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Paper>

          <div className={styles.authActions}>
            <Button className={styles.loginBtn}>Log in</Button>
            <Button className={styles.signupBtn}>Sign up</Button>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        {/* Filter Bar */}
        <div className={styles.filtersBar} style={{ marginTop: '24px' }}>
          <Button startIcon={<TuneIcon />} variant="outlined" className={styles.filterBtn}>Filters</Button>
          <Button 
            variant="outlined" 
            className={`${styles.filterBtn} ${radius === 10 ? styles.activeFilter : ''}`} 
            onClick={() => {
              setRadius(10);
              if (selectedLocation?.latitude) {
                fetchShops(selectedLocation.latitude, selectedLocation.longitude, 10, selectedLocation.id);
              }
            }}
          >
            10km
          </Button>
          <Button 
            variant="outlined" 
            className={`${styles.filterBtn} ${radius === 20 ? styles.activeFilter : ''}`} 
            onClick={() => {
              setRadius(20);
              if (selectedLocation?.latitude) {
                fetchShops(selectedLocation.latitude, selectedLocation.longitude, 20, selectedLocation.id);
              }
            }}
          >
            20km
          </Button>
          <Button 
            variant="outlined" 
            className={`${styles.filterBtn} ${radius === 50 ? styles.activeFilter : ''}`} 
            onClick={() => {
              setRadius(50);
              if (selectedLocation?.latitude) {
                fetchShops(selectedLocation.latitude, selectedLocation.longitude, 50, selectedLocation.id);
              }
            }}
          >
            50km
          </Button>
          <Button 
            variant="outlined" 
            className={`${styles.filterBtn} ${radius === 250 ? styles.activeFilter : ''}`} 
            onClick={() => {
              setRadius(250);
              if (selectedLocation?.latitude) {
                fetchShops(selectedLocation.latitude, selectedLocation.longitude, 250, selectedLocation.id);
              }
            }}
          >
            250km
          </Button>
          <Button variant="outlined" className={styles.filterBtn}>Rating: 4.0+</Button>
          <Button variant="outlined" className={styles.filterBtn}>Open Now</Button>
          <Button variant="outlined" className={styles.filterBtn}>Categories <ArrowDropDownIcon /></Button>
        </div>

        {/* Shop Grid */}
        <section className={styles.shopsSection}>
          <h2 className={styles.sectionTitle}>Nearest Shops</h2>
          <div className={styles.grid}>
            {loading ? (
              Array.from(new Array(8)).map((_, i) => (
                <div key={i} className={styles.skeletonCard}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
                  <Skeleton variant="text" sx={{ mt: 1, height: 30 }} />
                  <Skeleton variant="text" width="60%" />
                </div>
              ))
            ) : (
              shops
                .filter((shop) => {
                  const search = searchKey.toLowerCase();
                  if (!search) return true;

                  const nameMatch = (shop.name || shop.shop_name || '').toLowerCase().includes(search);
                  const pinMatch = (shop.pincode && String(shop.pincode).includes(search)) || (shop.pin_code && String(shop.pin_code).includes(search));
                  const locMatch = (shop.location || '').toLowerCase().includes(search) || (shop.city || '').toLowerCase().includes(search);
                  const catMatch = (shop.category || '').toLowerCase().includes(search);
                  const addressMatch = (shop.address || '').toLowerCase().includes(search);

                  return nameMatch || pinMatch || locMatch || catMatch || addressMatch;
                })
                .map((shop) => (
                  <div
                    key={shop.shopId || shop.id}
                    className={styles.shopCard}
                    onClick={() => handleShopClick(shop.shopId || shop.id)}
                  >
                    <div className={styles.cardImgWrapper}>
                      <img
                        src={shop.cover_image || shop.shop_logo || shop.image}
                        alt={shop.name || shop.shop_name}
                        className={styles.cardImg}
                      />
                      {shop.discount && <span className={styles.discountBadge}>{shop.discount}</span>}
                    </div>
                    <div className={styles.cardInfo}>
                      <div className={styles.cardHeader}>
                        <h3 className={styles.shopName}>{shop.name || shop.shop_name}</h3>
                        <div className={styles.ratingBadge}>
                          {shop.rating || ''} <StarIcon sx={{ fontSize: 12 }} />
                        </div>
                      </div>
                      <div className={styles.cardDetails}>
                        <span className={styles.cuisines}>{shop.category || 'General Store'}</span>
                        <span className={styles.price}>{(shop.distance !== null && shop.distance !== undefined) ? `${shop.distance} km` : 'Near you'}</span>
                      </div>
                    </div>
                  </div>
                ))
            )}
            {!loading && shops.length === 0 && (
              <div className={styles.noShops}>
                {selectedLocation ? (
                  <p>No shops found near you yet.</p>
                ) : (
                  <p>Please select a location above to discover nearby shops.</p>
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <img src={SanoftLogo} alt="Logo" className={styles.footerLogo} />
          <p>© 2026 Sanoft DigiMenu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default ShopListPage;
